"use client";
/**
 * The symbols the user is one click away from charting.
 *
 * `prefetchAround` has always accepted a `neighbourSymbols` list and warmed the
 * bar cache for each — but nothing ever passed one, so that half of it was dead
 * and every watchlist click paid a cold fetch. The measured gap is large: a cold
 * switch is ~230-830ms (almost entirely fetch) against ~26ms served from cache.
 *
 * This is a tiny module-scoped publish/subscribe rather than a fetch: the
 * watchlist pane already loads the rows for its own rendering, so it just
 * announces them. No extra request, and a desk with no watchlist open simply
 * gets an empty list and behaves exactly as before.
 *
 * Deliberately NOT React state — publisher and subscriber are in different
 * subtrees (the right rail vs the chart cell), and threading this through props
 * would mean lifting the watchlist into every page that hosts a chart.
 */

let current: ReadonlyArray<string> = [];
const listeners = new Set<() => void>();

/** Announce the symbols on screen. Cheap and idempotent — a no-op when the set
 *  is unchanged, so a re-render storm can't cause a prefetch storm. */
export function publishNeighbourSymbols(symbols: ReadonlyArray<string>): void {
  const next = [...new Set(symbols.map((s) => s.toUpperCase().trim()).filter(Boolean))];
  if (next.length === current.length && next.every((s, i) => s === current[i])) return;
  current = next;
  for (const fn of listeners) fn();
}

export function getNeighbourSymbols(): ReadonlyArray<string> {
  return current;
}

export function subscribeNeighbourSymbols(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
